import { FieldFormattingVersion, GenericQueryBuilderRule, } from './advancedSearchApiTypes';
import { QueryBuilderRuleValue, BooleanOperator, FilterInfoOperator, FormattingOperator, ListFieldFormatting, ColorRangeFormatting, EMPTY, COLOR_RANGE_KIND, } from './apiTypes';
import { Extend } from './internal/typeHelpers';
/**
 * `Note that this is not a complete list`
 */
export enum FilterTypes {
    /**
     * A filter type that looks up attributes on a component or reference, or it
     * can be a SpecialAttributeType.
     */
    ATTRIBUTE = 'attribute',
    /**
     * A filter type that looks up tags.
     */
    TAG = 'tag',
    SYSTEM_FILTER = 'system_filter',
    /** @deprecated Now uses individual attribute filters */
    DATE_RANGE = 'date_range',
    REFERENCE_LABEL = 'refLabel',
    COMPONENT_LABEL = 'compLabel'
}
/**
 * Number-based system filter types, for easier URL encoding and parsing.
 */
export enum SystemFilterType {
    DEFAULT_COLOR = '0'
}
/**
 * A "Comparator Matcher" is the basis for:
 * - conditional formatting
 * - client filtering
 *
 * LabelFormatting is similar, but doesn't use a comparator so it is not
 * included here because we want to further distinguish between the two.
 */
export type ComparatorMatcher = {
    affectComponent: boolean;
    affectReference: boolean;
    comparator?: FormattingOperator;
    isNegative: boolean;
    name: string;
    type: FilterTypes;
    value: QueryBuilderRuleValue;
    color?: string;
    // TODO(typeName-migration): Remove affectsTypeName after BE completes migration and old URLs are no longer supported.
    affectsTypeName?: string;
    affectsTypeNames?: string[];
};
/**
 * Different from FilterAttributes in that it is stricter and does not support
 * nested rules, eg. Advanced Search.
 *
 * These attributes are a mixed union of the attributes from:
 * - Conditional (Color) Formatting
 * - Label Formatting "Filters"
 */
export type FilterInterfaceFilter = Extend<ComparatorMatcher, {
    id?: string;
    value: string;
    includeTime?: boolean;
    isCollapsed?: boolean;
    // TODO(typeName-migration): Remove affectsTypeName after BE completes migration and old URLs are no longer supported.
    affectsTypeName?: string; // single-type scope (legacy; kept for backward compat)
    affectsTypeNames?: string[]; // multi-type scope; takes precedence when present
    version?: FieldFormattingVersion;
  }>;
// typia compiles the JSDoc here into the AI conditional-formatting tool's JSON
// schema (via AiFormattingFilter), so these comments are LLM-facing — keep them accurate.
export type FormattingFilter = Extend<FilterInterfaceFilter, {
    /** `true` if the rule colours components. */
    affectComponent: boolean;
    /** `true` if the rule colours references. A tag rule sets both `affectComponent` and `affectReference` to `true` with `type: "tag"`. */
    affectReference: boolean;
    /** `"attribute"` for a rule that tests a component or reference field; `"tag"` for a tag rule that matches by the tag named in `value`. */
    type: FilterTypes;
    /** How the field is compared against the rule's `value`. The required shape of `value` depends on this comparator — see each comparator. */
    comparator?: FormattingOperator;
    /**
     * Name of the field the rule tests (for example `cost` or `lifecycle`), or
     * the literal `"type"` to test the entity's own type — colour a whole type
     * with `name: "type"`, `comparator: "idMatch"`, and the type name as `value`.
     */
    name: string;
    /** The component or reference type the rule is limited to (e.g. `Application`), or omitted to apply to every component or reference regardless of type. */
    affectsTypeName?: string;
    /**
     * The component or reference type(s) the rule is limited to — e.g.
     * `["Application"]`, or `["Application", "Server"]` to scope one rule to
     * several types. Omit to apply to every component or reference regardless of type.
     * A pre-existing legacy rule may instead carry a singular `affectsTypeName`;
     * when it does, reproduce that scope here as a single-element array (e.g.
     * `affectsTypeName: "Application"` → `affectsTypeNames: ["Application"]`).
     */
    affectsTypeNames?: string[];
    /** The value the field is compared against; its required shape is determined by the comparator (see each comparator). For a `tag` rule this is the tag name. */
    value: QueryBuilderRuleValue;
    /**
     * The colour painted on the entities this rule matches, as a hex string. Choose from Ardoq's
     * conditional-formatting palette, each hue running light → dark:
     * reds/oranges `#FFCFC5 #FF7E64 #ED3915 #A5280F #4B1004`,
     * greens `#BDF5DE #4CE5A6 #09B06B #0F6C45 #03301D`,
     * yellows `#FEE685 #FFD230 #FE9A00 #BB4D00 #461901`,
     * blues `#BFDBFE #60A5FA #2563EB #1E40AF #172554`,
     * purples `#DECDFF #AF73FF #8800FF #4C008F #1F003B`,
     * pinks `#FFCFFA #FB5FE1 #D623B3 #911775 #4F033C`,
     * greys `#E6E7E9 #A1A3A9 #70737B #43454B #040506`.
     * Prefer a hue's mid shade for highlights; when the user names a colour, use the mid shade of the closest hue. Match the user's intent (e.g. red for risk, green for healthy).
     */
    color: string;
  }>;
/**
 * FilterAttributes is the core building block of Perspectives in Ardoq, and powers:
 *  - Conditional formatting (WS & VP mode, flat rules)
 *  - Label formatting (WS & VP mode, flat rules)
 *  - Workspace filters (WS mode, flat rules)
 *  - Filtering (WS mode, nested rules)
 *
 * They are being stored on the backend as part of perspectives, viewpoints, traversals and slides.
 *
 * The concept also maps 1:1 to query builder rules, which powers the UI for WS mode
 * perspective manager. `filterUtils` provides functions to convert back and forth.
 *
 * All attributes in this type are optional, because Backbone allows creating a model
 * without any arguments - in which case the default attributes will be applied.
 */
export interface FilterAttributes {
    condition?: BooleanOperator;
    affectComponent?: boolean;
    affectReference?: boolean;
    type?: FilterTypes;
    comparator?: FormattingOperator;
    value?: QueryBuilderRuleValue;
    isNegative?: boolean;
    isCollapsed?: boolean;
    name?: string;
    color?: string;
    includeTime?: boolean;
    affectsTypeName?: string;
    affectsTypeNames?: string[];
    showFieldName?: boolean;
    version?: FieldFormattingVersion;
    rules?: FilterAttributes[];
}
/**
 * FilterAttributes version of QueryBuilderSubquery, so its only significance
 * is to store the nesting of rules.
 * In practice this has more attributes, but we consider them deprecated as they
 * are unnecessary.
 * NOTE: WorkspaceFilterAttributes also conform to this type, but doesn't nest.
 */
export type FilterAttributesSubquery = Required<Pick<FilterAttributes, 'rules' | 'condition'>>;
export type WorkspaceFilterAttributes = Extend<FilterAttributes, {
    rules: FilterInterfaceFilter[]; // Flat
    condition: BooleanOperator;
  }>;
export interface FiltersBooleanRuleLegacy extends GenericQueryBuilderRule, FiltersBooleanRuleDataExtension {
}
/**
 * @deprecated suspected dead-code
 */
export type FiltersBooleanRuleDateRangeData = {
    isDateRange: boolean;
    dateRangeStartFieldName: string;
    dateRangeEndFieldName: string;
};
/**
 * @deprecated (?) Only used with deprecated dateRangeRule
 */
export type FiltersBooleanRuleDataExtension = {
    data?: FiltersBooleanRuleDateRangeData | {
        cid: string;
    };
};
// ============================================================================
// Label Formatting
export type LabelFormattingBaseAttributes = {
    affectComponent: boolean;
    affectReference: boolean;
    isNegative: boolean;
    /**
     * Date fields can optionally be formatted with time
     */
    includeTime?: boolean;
    name: '';
    value: string;
    affectsTypeName?: string;
    showFieldName?: boolean;
};
export type ComponentLabelFormattingAttributes = Extend<LabelFormattingBaseAttributes, {
    affectComponent: true;
    affectReference: false;
    type: FilterTypes.COMPONENT_LABEL;
  }>;
export type ReferenceLabelFormattingAttributes = Extend<LabelFormattingBaseAttributes, {
    affectComponent: false;
    affectReference: true;
    type: FilterTypes.REFERENCE_LABEL;
  }>;
export type LabelFormattingAttributes = ComponentLabelFormattingAttributes | ReferenceLabelFormattingAttributes;
// ============================================================================
// Conditional Formatting aka. "Color Filters"
export type ConditionalFormattingAttributes = TagConditionalFormattingAttributes | ReferenceConditionalFormattingAttributes | ComponentConditionalFormattingAttributes;
/**
 * Base attributes for type: FilterTypes.ATTRIBUTE
 */
type BaseAttributeConditionalFormattingAttributes = {
    color: string;
    comparator: FilterInfoOperator;
    isNegative: boolean;
    name: string;
    type: FilterTypes.ATTRIBUTE;
    value: QueryBuilderRuleValue;
};
export type ReferenceConditionalFormattingAttributes = BaseAttributeConditionalFormattingAttributes & {
    affectComponent: false;
    affectReference: true;
};
export type ComponentConditionalFormattingAttributes = BaseAttributeConditionalFormattingAttributes & {
    affectComponent: true;
    affectReference: false;
};
/**
 * Conditional formatting based on tags
 * Important distinction from its attribute counterpart is:
 * - Applies to both components and references
 * - Value is always a string
 * - Doesn't have a comparator
 */
export type TagConditionalFormattingAttributes = {
    affectComponent: true;
    affectReference: true;
    color: string;
    isNegative: boolean;
    name: string;
    type: FilterTypes.TAG;
    value: string;
};
export type SystemFilterAttributes = {
    affectComponent?: boolean;
    affectReference?: boolean;
    type: FilterTypes.SYSTEM_FILTER;
    name: SystemFilterType;
    value?: string;
    color?: string;
};
const hasColor = (attributes: FilterAttributes): boolean => {
    return Boolean((attributes as ConditionalFormattingAttributes).color);
};
const hasRulesAndCondition = (attributes: FilterAttributes): attributes is FilterAttributesSubquery => {
    const { rules, condition } = attributes;
    return Array.isArray(rules) && Boolean(condition);
};
const isType = (attributes: FilterAttributes, type: FilterTypes): boolean => {
    return attributes.type === type;
};
const affectsComponent = (attributes: FilterAttributes): boolean => {
    return Boolean(attributes.affectComponent);
};
const affectsReference = (attributes: FilterAttributes): boolean => {
    return Boolean(attributes.affectReference);
};
/**
 * This predicate determines if a set of filter attributes can be parsed as
 * QueryBuilderRow
 */
export const isComponentFilter = (attributes: FilterAttributes) => !hasColor(attributes) &&
    affectsComponent(attributes) &&
    !isType(attributes, FilterTypes.COMPONENT_LABEL);
/**
 * This predicate determines if a set of filter attributes can be parsed as
 * QueryBuilderRow
 */
export const isReferenceFilter = (attributes: FilterAttributes) => !hasColor(attributes) &&
    affectsReference(attributes) &&
    !isType(attributes, FilterTypes.REFERENCE_LABEL);
export const isFormatting = (attributes: FilterAttributes): attributes is ConditionalFormattingAttributes | LabelFormattingAttributes => hasColor(attributes) ||
    isType(attributes, FilterTypes.REFERENCE_LABEL) ||
    isType(attributes, FilterTypes.COMPONENT_LABEL);
export const isConditionalFormatting = (attributes: FilterAttributes): attributes is ConditionalFormattingAttributes => hasColor(attributes) &&
    !isType(attributes, FilterTypes.REFERENCE_LABEL) &&
    !isType(attributes, FilterTypes.COMPONENT_LABEL);
export const isFormattingFilter = (filter: FormattingFilter | FilterInterfaceFilter): filter is FormattingFilter => isConditionalFormatting(filter);
export const isListFieldFormatting = (value: unknown): value is ListFieldFormatting => typeof value === 'object' && value !== null && EMPTY in value;
export const isColorRangeFormatting = (value: unknown): value is ColorRangeFormatting => typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    value.kind === COLOR_RANGE_KIND;
export const isSystemFilter = (attributes: FilterAttributes): attributes is SystemFilterAttributes => isType(attributes, FilterTypes.SYSTEM_FILTER);
export const isComponentConditionalFormatting = (attributes: FilterAttributes): attributes is ComponentConditionalFormattingAttributes => isConditionalFormatting(attributes) &&
    affectsComponent(attributes) &&
    isType(attributes, FilterTypes.ATTRIBUTE);
export const isReferenceConditionalFormatting = (attributes: FilterAttributes): attributes is ComponentConditionalFormattingAttributes => isConditionalFormatting(attributes) &&
    affectsReference(attributes) &&
    isType(attributes, FilterTypes.ATTRIBUTE);
export const isTagConditionalFormatting = (attributes: FilterAttributes): attributes is TagConditionalFormattingAttributes => hasColor(attributes) && isType(attributes, FilterTypes.TAG);
export const isReferenceLabelFormatting = (attributes: FilterAttributes): attributes is ReferenceLabelFormattingAttributes => isType(attributes, FilterTypes.REFERENCE_LABEL);
export const isComponentLabelFormatting = (attributes: FilterAttributes): attributes is ComponentLabelFormattingAttributes => isType(attributes, FilterTypes.COMPONENT_LABEL);
export const filterAttributesIsSubquery = (filter: FilterAttributes): filter is FilterAttributesSubquery => {
    return hasRulesAndCondition(filter);
};
/**
 * When stored as FilterAttributes "workspace filters" are the same as
 * sub-queries, the difference is that they are not nested.
 */
export const isWorkspaceFilter = (attributes: FilterAttributes): attributes is WorkspaceFilterAttributes => {
    if (!hasRulesAndCondition(attributes))
        return false;
    return Boolean(attributes.rules?.every(rule => rule.name === 'rootWorkspace'));
};
